/**
 * WalletContext.jsx
 * 
 * This context provides wallet functionality to all components in the application.
 * It's used for integrating with Solana wallets and NFT minting.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the wallet context
const WalletContext = createContext(null);

/**
 * WalletProvider component
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Wallet provider component
 */
export const WalletProvider = ({ children }) => {
  // For now, we'll use a simulated wallet
  // In production, this would be replaced with a real wallet connection
  const [simulatedWallet, setSimulatedWallet] = useState(() => {
    const saved = localStorage.getItem('simulated_wallet');
    const initialValue = saved ? JSON.parse(saved) : null;
    return initialValue || {
      publicKey: `wallet_${Math.random().toString(36).substring(2, 9)}`,
      isConnected: false
    };
  });
  
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Save wallet to localStorage
  useEffect(() => {
    localStorage.setItem('simulated_wallet', JSON.stringify(simulatedWallet));
  }, [simulatedWallet]);
  
  // Connect to wallet
  const connect = async () => {
    try {
      setIsConnecting(true);
      
      // Simulate wallet connection
      // In production, this would be replaced with a real wallet connection
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate delay
      
      setSimulatedWallet({
        ...simulatedWallet,
        isConnected: true
      });
      
      return true;
    } catch (error) {
      console.error('Error connecting to wallet:', error);
      return false;
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Disconnect from wallet
  const disconnect = async () => {
    try {
      // Simulate wallet disconnection
      // In production, this would be replaced with a real wallet disconnection
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
      
      setSimulatedWallet({
        ...simulatedWallet,
        isConnected: false
      });
      
      return true;
    } catch (error) {
      console.error('Error disconnecting from wallet:', error);
      return false;
    }
  };
  
  // Create the wallet object
  const wallet = {
    publicKey: simulatedWallet.isConnected ? simulatedWallet.publicKey : null,
    isConnected: simulatedWallet.isConnected,
    connect,
    disconnect,
    isConnecting
  };
  
  // Provide the wallet context
  return (
    <WalletContext.Provider value={wallet}>
      {children}
    </WalletContext.Provider>
  );
};

/**
 * useWallet hook
 * 
 * @returns {Object} - Wallet context
 */
export const useWallet = () => {
  const context = useContext(WalletContext);
  
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  
  return context;
};
